import {type ThunkDispatch, type UnknownAction} from '@reduxjs/toolkit';
import {showNotification, useEventListener} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector, useStore} from 'react-redux';

import {insert, isEmpty, isNil, isNotNil, map, omit, pick} from 'ramda';

import {
  newVehicleApi,
  usePutTrimFeatureAssignmentsMutation,
  type FeatureResponseBody,
  type PricelistVersionRequestBody,
  type TrimFeatureAssignment,
  type TrimFeatureAssignmentInput,
  type TrimResponseBody,
} from '@dms/api/new-vehicle';

import type {ApiError, Nullish} from 'shared/web';

import {
  ITEM_CHANGE_CANCELLED,
  type PricedItemChangeOutcome,
} from '../types/PricedItemChangeOutcome';
import {handleCatalogueError, t} from '../utils/catalogueUi';
import {isInPricelist} from '../utils/isInPricelist';
import {priceInput} from '../utils/moneyInput';
import {setTrimInclusion} from '../utils/setTrimInclusion';
import {type CatalogueState} from './useMatrixSaveQueue';
import {useTrimBatchSave, type WriteTrims} from './useTrimBatchSave';

export type MatrixTrim = Pick<TrimResponseBody, 'id' | 'name' | 'version' | 'publicationStatus'>;
export type CellValue = TrimFeatureAssignmentInput['inclusion'] | 'NOT_OFFERED';
/** A cell's status and, when Optional, its price on the trim as typed ('' until entered). */
export type CellDraft = {value: CellValue; priceNet: string};
/** The last cell the author changed, so the matrix can announce it. */
type CellChange = {
  trim: MatrixTrim;
  feature: FeatureResponseBody;
  value: CellValue;
  isUnsaved: boolean;
};
/** A trim's write, prepared on a fresh read. */
export type TrimWrite = {
  trim: TrimResponseBody;
  stored: TrimFeatureAssignment[];
  next: TrimFeatureAssignmentInput[];
  /** Sends the PUT; `pricelistVersion` is set only after the new pricelist version dialog. */
  put: (pricelistVersion?: PricelistVersionRequestBody) => Promise<TrimFeatureAssignment[]>;
};
/** Sends a trim's write, on a published year after a new pricelist version dialog (D88). */
export type WriteTrim = (write: TrimWrite) => Promise<PricedItemChangeOutcome>;
type ReplaceAssignments = (
  trimId: string,
  update: (assignments: TrimFeatureAssignment[]) => TrimFeatureAssignmentInput[],
  write: WriteTrim
) => Promise<{outcome: PricedItemChangeOutcome; saved?: TrimFeatureAssignment[]}>;
type TrimSaveOutcome = PricedItemChangeOutcome | 'failed';
/** Per trim id, per feature id: a value the author picked that is not saved yet. */
type Drafts = Record<string, Record<string, CellDraft>>;

export const serverCell = (assignment: TrimFeatureAssignment | Nullish): CellDraft => ({
  value: assignment?.inclusion ?? 'NOT_OFFERED',
  priceNet: assignment?.priceNet ?? '',
});
const parsedPrice = (draft: CellDraft) => priceInput.safeParse(draft.priceNet).data;
/** An Optional cell is sold at its price, so it cannot be saved without one. */
export const isPriceMissing = (draft: CellDraft) =>
  draft.value === 'OPTIONAL' && isNil(parsedPrice(draft));
const isSameCell = (a: CellDraft, b: CellDraft) =>
  match({isSameValue: a.value === b.value, isOptional: a.value === 'OPTIONAL'})
    .with({isSameValue: false}, () => false)
    .with({isSameValue: true, isOptional: false}, () => true)
    .with({isSameValue: true, isOptional: true}, () => {
      const [priceA, priceB] = [parsedPrice(a), parsedPrice(b)];
      return isNil(priceA) || isNil(priceB)
        ? a.priceNet === b.priceNet
        : Number(priceA) === Number(priceB);
    })
    .exhaustive();
const isFreeChoice = (draft: CellDraft) => {
  const price = parsedPrice(draft);
  return isNotNil(price) && Number(price) === 0;
};
const isUnsaved = (savingDraft: CellDraft | Nullish, draft: CellDraft, server: CellDraft) =>
  !isSameCell(draft, server) || isNotNil(savingDraft);
const withDraft = (
  drafts: Drafts,
  trimId: string,
  featureId: string,
  draft: CellDraft,
  isKept: boolean
): Drafts => {
  const trimDrafts = omit([featureId], drafts[trimId] ?? {});
  return {...drafts, [trimId]: isKept ? {...trimDrafts, [featureId]: draft} : trimDrafts};
};

/** The trim's fresh assignments with its drafts applied. */
const applyDrafts = (
  trimDrafts: Record<string, CellDraft>,
  freshAssignments: TrimFeatureAssignment[]
) =>
  Object.entries(trimDrafts).reduce<TrimFeatureAssignmentInput[]>(
    (assignments, [featureId, draft]) =>
      draft.value === 'NOT_OFFERED'
        ? assignments.filter((assignment) => assignment.featureId !== featureId)
        : setTrimInclusion(assignments, featureId, draft.value, parsedPrice(draft) ?? null),
    freshAssignments
  );

const withFeature = (feature: FeatureResponseBody) => (features: FeatureResponseBody[]) =>
  features.some(({id}) => id === feature.id) ? features : [...features, feature];
// The backend lists a model year's items by name, then id.
const byName = (a: FeatureResponseBody, b: FeatureResponseBody) =>
  a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
// A grouped trim counts as saved: the open group holds its change until confirm or discard.
const SAVED_OUTCOMES = new Set<string>(['written', 'writtenWithVersion', 'grouped']);
const isNotFound = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 404;

/**
 * The equipment-by-trim matrix's unsaved cell values and the writes that save them. Saves run one
 * at a time across the whole matrix and each trim's save starts from a fresh read, so a value
 * cycled through Not offered never deletes an assignment's price and conditions.
 */
export function useEquipmentMatrixDrafts(props: {
  modelYearId: string;
  trims: MatrixTrim[];
  /** The model year's items. */
  features: FeatureResponseBody[];
  writeTrim: WriteTrim;
  writeTrims: WriteTrims;
  /** Whether the model year is published; a save of several trims of it is one batch. */
  readIsPublished: () => Promise<boolean>;
}) {
  const store = useStore<CatalogueState>();
  const dispatch = useDispatch<ThunkDispatch<CatalogueState, unknown, UnknownAction>>();
  const [putAssignments] = usePutTrimFeatureAssignmentsMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const [getFeature] = newVehicleApi.useLazyGetFeatureQuery();
  // Every assignment write of the matrix, one at a time: the backend rejects a trim's PUT as stale
  // while another trim of the model year is being written (DMS-NV-CT-0012). A save of several
  // trims of a published year is one batch request, which records one version.
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  // Per trim, the drafts the running save is writing, from the moment the save starts. The cache
  // holds the values from before that save until it confirms them, so meanwhile a click on one of
  // those cells always stays a draft.
  const savingDrafts = useRef(new Map<string, Record<string, CellDraft>>());
  // Each cell reads its own trim's list; the row actions need every trim's.
  const trimAssignments = useSelector(
    (state: CatalogueState) =>
      props.trims.map(
        ({id}) =>
          newVehicleApi.endpoints.listTrimFeatureAssignments.select({trimId: id})(state).data
      ),
    shallowEqual
  );
  const [allDrafts, setDrafts] = useState<Drafts>({});
  const [isSaving, setSaving] = useState(false);
  const [lastChange, setLastChange] = useState<CellChange | null>(null);
  // Items the author changed or added stay rows, in their place, until a save fully succeeds and
  // the year's list is read again: a save that moves an item between trims can take it off that
  // list for a moment, which must not hide its other drafts. A trim archived meanwhile drops out of
  // the matrix, and so do its drafts.
  const [pinnedFeatures, setPinnedFeatures] = useState<FeatureResponseBody[]>([]);
  // Items changed or added since the latest save started; that save does not unpin them.
  const touchedSinceSave = useRef(new Set<string>());
  // Items added from the library and not saved on a trim yet: only Discard unpins them.
  const libraryAdds = useRef(new Set<string>());
  const saveRun = useRef(0);
  const features = pinnedFeatures
    .filter((pinned) => !props.features.some(({id}) => id === pinned.id))
    .reduce((rows, pinned) => {
      const at = rows.findIndex((row) => byName(pinned, row) < 0);
      return at === -1 ? [...rows, pinned] : insert(at, pinned, rows);
    }, props.features);
  const featureIds = features.map(({id}) => id);
  const drafts: Drafts = Object.fromEntries(
    props.trims.map(({id}) => [id, pick(featureIds, allDrafts[id] ?? {})])
  );
  const draftCount = Object.values(drafts).flatMap(Object.keys).length;
  // A library row set on no trim yet is a pending change too: only Discard takes it away.
  const unsetLibraryRows = features.filter(
    ({id}) =>
      libraryAdds.current.has(id) && !Object.values(drafts).some((trimDrafts) => id in trimDrafts)
  );
  const pendingCount = draftCount + unsetLibraryRows.length;
  useEventListener(window, 'beforeunload', (event) => {
    if (pendingCount > 0) {
      event.preventDefault();
    }
  });

  // replaceAssignments and setDraft keep their identity so a click re-renders only its own cell:
  // re-rendering all 630 cells of a 63 x 10 matrix took about 0.7 s per click (dev build).
  const replaceAssignments = useCallback<ReplaceAssignments>(
    (trimId, update, write) => {
      const operation: ReturnType<ReplaceAssignments> = writes.current
        .catch(() => undefined)
        .then(async () => {
          const trim = await getTrim({trimId}, false).unwrap();
          await getAssignments({trimId}, true);
          const assignments = await getAssignments({trimId}, false).unwrap();
          const next = update(assignments);
          let saved: TrimFeatureAssignment[] | undefined;
          // The next trim's write waits for this one's dialog, so its version label is the next one.
          const outcome = await write({
            trim,
            stored: assignments,
            next,
            put: (pricelistVersion) =>
              putAssignments({
                trimId,
                replaceTrimFeatureAssignmentsRequestBody: {
                  assignments: next,
                  version: trim.version,
                  ...(pricelistVersion && {pricelistVersion}),
                },
              })
                .unwrap()
                .then((result) => {
                  saved = result;
                  return result;
                }),
          });
          return {outcome, saved};
        });
      writes.current = operation;
      return operation;
    },
    [getTrim, getAssignments, putAssignments]
  );

  const setDraft = useCallback(
    (trim: MatrixTrim, feature: FeatureResponseBody, draft: CellDraft, server: CellDraft) => {
      touchedSinceSave.current.add(feature.id);
      setPinnedFeatures(withFeature(feature));
      const isKept =
        isUnsaved(savingDrafts.current.get(trim.id)?.[feature.id], draft, server) ||
        (draft.value === 'OPTIONAL' && draft.priceNet !== server.priceNet);
      setDrafts((current) => withDraft(current, trim.id, feature.id, draft, isKept));
      setLastChange({trim, feature, value: draft.value, isUnsaved: isKept});
    },
    []
  );

  const retireStoredDrafts = (trimId: string, stored: TrimFeatureAssignment[]) => {
    const storedCell = (featureId: string) =>
      serverCell(stored.find((assignment) => assignment.featureId === featureId));
    setDrafts((current) => ({
      ...current,
      [trimId]: Object.fromEntries(
        Object.entries(current[trimId] ?? {}).filter(
          ([featureId, draft]) => !isSameCell(storedCell(featureId), draft)
        )
      ),
    }));
  };

  // A grouped write sent nothing: its drafts retire, as the group bar holds the change (spec 7.2).
  const retireGroupedDrafts = (trimId: string, trimDrafts: Record<string, CellDraft>) => {
    Object.keys(trimDrafts).forEach((id) => libraryAdds.current.delete(id));
    setDrafts((current) => ({
      ...current,
      [trimId]: Object.fromEntries(
        Object.entries(current[trimId] ?? {}).filter(
          ([featureId, draft]) =>
            isNil(trimDrafts[featureId]) || !isSameCell(trimDrafts[featureId], draft)
        )
      ),
    }));
  };

  const retireCachedDrafts = (trimId: string) =>
    retireStoredDrafts(
      trimId,
      newVehicleApi.endpoints.listTrimFeatureAssignments.select({trimId})(store.getState()).data ??
        []
    );

  const saveTrim = (
    trimId: string,
    trimDrafts: Record<string, CellDraft>
  ): Promise<TrimSaveOutcome> => {
    savingDrafts.current.set(trimId, trimDrafts);
    return replaceAssignments(
      trimId,
      (freshAssignments) => applyDrafts(trimDrafts, freshAssignments),
      props.writeTrim
    )
      .then(({outcome, saved}) => {
        if (outcome === 'grouped') {
          retireGroupedDrafts(trimId, trimDrafts);
          return outcome;
        }
        // A cancelled new pricelist version sent nothing: the trim keeps its drafts.
        if (isNil(saved)) {
          return outcome;
        }
        // The saved set goes into the cache before the drafts retire, so no cell flickers back.
        dispatch(
          newVehicleApi.util.updateQueryData('listTrimFeatureAssignments', {trimId}, () => saved)
        );
        // A library row saved on this trim is set, whether or not the other trims save.
        Object.keys(trimDrafts).forEach((id) => libraryAdds.current.delete(id));
        retireStoredDrafts(trimId, saved);
        return outcome;
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error);
        retireCachedDrafts(trimId);
        return 'failed' as const;
      })
      .finally(() => savingDrafts.current.delete(trimId));
  };

  const batch = useTrimBatchSave<Record<string, CellDraft>>({
    writes,
    writeTrims: props.writeTrims,
    applyDrafts,
    onTrimSaved: (trimId, trimDrafts, saved) => {
      Object.keys(trimDrafts).forEach((id) => libraryAdds.current.delete(id));
      retireStoredDrafts(trimId, saved);
    },
    onTrimGrouped: retireGroupedDrafts,
    onTrimFailed: retireCachedDrafts,
  });

  const findArchived = async (draftedIds: string[]) => {
    const outsideYear = pinnedFeatures.filter(
      ({id}) => draftedIds.includes(id) && !props.features.some((feature) => feature.id === id)
    );
    const reads = await Promise.all(outsideYear.map(({id}) => getFeature({featureId: id}, false)));
    return outsideYear.filter((_, index) => isNotFound(reads[index]?.error));
  };

  const save = async () => {
    // The pending bar and the leave dialog can both ask to save; only one save runs at a time.
    if (isSaving) {
      return false;
    }
    // A save writes no library row set on no trim, so leaving after it would drop them: name them
    // and report the save incomplete, whether or not there were drafts to write.
    const keepsUnsetLibraryRows = () => {
      if (unsetLibraryRows.length === 0) {
        return false;
      }
      showNotification.error(
        t(
          'entity.newVehicle.catalogue.libraryRowsUnset',
          'Set {names} on a trim, or discard the changes.',
          {names: unsetLibraryRows.map(({name}) => name).join(', ')}
        ),
        {errorOrigin: 'business'}
      );
      return true;
    };
    if (draftCount === 0 && keepsUnsetLibraryRows()) {
      return false;
    }
    if (
      Object.values(drafts).some((trimDrafts) => Object.values(trimDrafts).some(isPriceMissing))
    ) {
      showNotification.error(
        t(
          'entity.newVehicle.catalogue.optionPricesMissing',
          'Enter a price in every Optional cell marked red, then save again.'
        ),
        {errorOrigin: 'business'}
      );
      return false;
    }
    setSaving(true);
    batch.clearFailedTrim();
    const run = saveRun.current + 1;
    saveRun.current = run;
    touchedSinceSave.current = new Set();
    const pinnedIds = pinnedFeatures.map(({id}) => id);
    const draftsToSave = Object.entries(drafts).filter(([, trimDrafts]) => !isEmpty(trimDrafts));
    draftsToSave.forEach(([trimId, trimDrafts]) => savingDrafts.current.set(trimId, trimDrafts));
    const archived = await findArchived(
      draftsToSave.flatMap(([, trimDrafts]) => Object.keys(trimDrafts))
    );
    const archivedIds = archived.map(({id}) => id);
    if (archived.length > 0) {
      setDrafts(map((trimDrafts) => omit(archivedIds, trimDrafts)));
      setPinnedFeatures((current) => current.filter(({id}) => !archivedIds.includes(id)));
      showNotification.warning(
        t(
          'entity.newVehicle.catalogue.archivedEquipmentDropped',
          '{names} was archived in the equipment library meanwhile; its unsaved changes were dropped.',
          {names: archived.map(({name}) => name).join(', ')}
        )
      );
    }
    const savedDrafts = draftsToSave
      .map(([trimId, trimDrafts]) => [trimId, omit(archivedIds, trimDrafts)] as const)
      .filter(([, trimDrafts]) => !isEmpty(trimDrafts));
    // Several trims of a published year are one batch with one pricelist version (D107). A draft
    // year, or one trim, saves trim by trim without a batch: no version, or only one, is recorded.
    const isOneBatch = savedDrafts.length > 1 && (await props.readIsPublished().catch(() => false));
    // Trim by trim, a cancelled version stops the save: that trim and every trim after it are not
    // sent and keep their drafts.
    const outcomes = isOneBatch
      ? [await batch.saveTrims(savedDrafts)]
      : await savedDrafts.reduce<Promise<TrimSaveOutcome[]>>(
          (previous, [trimId, trimDrafts]) =>
            previous.then((done) =>
              done.includes(ITEM_CHANGE_CANCELLED)
                ? done
                : saveTrim(trimId, trimDrafts).then((outcome) => [...done, outcome])
            ),
          Promise.resolve([])
        );
    draftsToSave.forEach(([trimId]) => savingDrafts.current.delete(trimId));
    setSaving(false);
    const isEverySaved = outcomes.every((outcome) => SAVED_OUTCOMES.has(outcome));
    // A trim saved with a new pricelist version already showed "Saved. Pricelist is now {label}."
    if (outcomes.length > 0 && outcomes.every((outcome) => outcome === 'written')) {
      showNotification.success(
        t('entity.newVehicle.catalogue.assignmentsSaved', 'Trim assignments saved')
      );
    }
    if (isEverySaved) {
      void dispatch(
        newVehicleApi.endpoints.listFeature.initiate(
          {modelYearId: props.modelYearId},
          {subscribe: false, forceRefetch: true}
        )
      ).then((result) => {
        if (!result.isSuccess || saveRun.current !== run) {
          return;
        }
        const touched = touchedSinceSave.current;
        setPinnedFeatures((current) =>
          current.filter(
            ({id}) => !pinnedIds.includes(id) || touched.has(id) || libraryAdds.current.has(id)
          )
        );
      });
    }
    return isEverySaved && !keepsUnsetLibraryRows();
  };

  const rowCells = (featureId: string) =>
    props.trims.map((trim, index) => ({
      trim,
      server: serverCell(trimAssignments[index]?.find((row) => row.featureId === featureId)),
      draft: drafts[trim.id]?.[featureId],
    }));
  /**
   * A stored Optional price of an online trim that stays Optional: on a published year only a price
   * adjustment changes it (D102). Every other Optional cell is a draft of its trim's next write.
   */
  const isKeptPrice = ({trim, server, draft}: ReturnType<typeof rowCells>[number]) =>
    isInPricelist.trim(trim.publicationStatus) &&
    server.value === 'OPTIONAL' &&
    !isFreeChoice(server) &&
    (draft ?? server).value === 'OPTIONAL';

  return {
    features,
    /** The item's price on another trim of the year, where a new Optional cell starts; never a free one (0). */
    startPrice: (featureId: string) =>
      rowCells(featureId)
        .map(({server, draft}) => draft ?? server)
        .find((cell) => cell.value === 'OPTIONAL' && !isPriceMissing(cell) && !isFreeChoice(cell))
        ?.priceNet,
    hasOptionalCell: (featureId: string) =>
      rowCells(featureId).some(({server, draft}) => (draft ?? server).value === 'OPTIONAL'),
    /**
     * Whether setting `priceNet` changes a kept price of the row, which only a price adjustment does.
     * A kept price that already is `priceNet` needs none (the adjustment would change nothing).
     */
    changesKeptPrice: (featureId: string, priceNet: string) =>
      rowCells(featureId).some(
        (rowCell) =>
          isKeptPrice(rowCell) && !isSameCell(rowCell.server, {value: 'OPTIONAL', priceNet})
      ),
    /**
     * "Same price on all trims": every Optional cell of the row, except the free ones (0), and on a
     * published year (`isPublished`) except the kept prices.
     */
    fillRow: (feature: FeatureResponseBody, priceNet: string, isPublished: boolean) => {
      touchedSinceSave.current.add(feature.id);
      setDrafts((current) =>
        rowCells(feature.id).reduce((next, rowCell) => {
          const {trim, server, draft} = rowCell;
          const cell = draft ?? server;
          if (
            cell.value !== 'OPTIONAL' ||
            isFreeChoice(cell) ||
            (isPublished && isKeptPrice(rowCell))
          ) {
            return next;
          }
          const filled = {value: cell.value, priceNet};
          return withDraft(
            next,
            trim.id,
            feature.id,
            filled,
            isUnsaved(savingDrafts.current.get(trim.id)?.[feature.id], filled, server)
          );
        }, current)
      );
    },
    pin: (feature: FeatureResponseBody) => {
      touchedSinceSave.current.add(feature.id);
      libraryAdds.current.add(feature.id);
      setPinnedFeatures(withFeature(feature));
    },
    drafts,
    draftCount,
    pendingCount,
    isSaving,
    lastChange,
    failedTrimId: batch.failedTrimId,
    setDraft,
    replaceAssignments,
    save,
    discard: () => {
      setDrafts({});
      batch.clearFailedTrim();
      setPinnedFeatures([]);
      libraryAdds.current.clear();
    },
  };
}
